/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Button,
  Box,
  Divider,
  Flex,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '../../../types/provision';
import { ProvisionApi } from '@Apis';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { reportPendo } from '@Utils/setupPendo';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { StepProps, Steps } from 'antd';
import i18next from 'i18next';
import { AxiosResponse } from 'axios';
import RejectProvisionModal from './rejectProvisionModal';
import { Service } from '@Types/app';
import { useProvisionEntityInfo } from '@Models/useProvisionEntityInfo';

enum CommonStatus {
  NotProvision = 0,
  Provisioning = 1,
  Provisioned = 2,
}
export type CommonProvisionModalState = ProvisionModalState & {
  service:
    | Service.OneDrive
    | Service.SharePoint
    | Service.Teams
    | Service.Box
    | Service.Dropbox;
};

/**
 * @description modal for sp、od、teams
 */
const CommonModal = ({
  service,
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: CommonProvisionModalState) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: CommonStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: CommonStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: CommonStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [commonStatus, setCommonStatus] = useState<CommonStatus>(
    // StatusMap[provisionStatus.status] || SharePointStatus.NotProvision
    () => {
      if (Object.keys(StatusMap).includes(provisionStatus.status)) {
        return StatusMap[provisionStatus.status as keyof typeof StatusMap];
      } else {
        //provisionStatus.status = failure
        //token failure
        if (provisionStatus.authorization.status === TokenStatus.Failure) {
          return CommonStatus.NotProvision;
        } else {
          //id sync fail
          return CommonStatus.Provisioning;
          //TODO: 同时设置 id sync 为失败的情况
        }
      }
    }
  );
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };

  const stepItems: StepProps[] = [
    {
      icon: <StepIcon checked={commonStatus >= CommonStatus.Provisioning} />,
      title: (
        <Text pt="4px" {...titleTextStyle}>
          {t(`provision.service.${service}.grantPermission`)}
        </Text>
      ),
      description:
        commonStatus === CommonStatus.NotProvision ? (
          <GrantPermissionAndEnablePolicy
            service={service}
            authenticationInfo={authenticationInfo}
            setCommonStatus={setCommonStatus}
            setErrorMsg={setErrorMsg}
            orgId={orgId}
          />
        ) : (
          <Text color={colorStyle.color.tertiary} pt="8px" lineHeight="20px">
            {t('provision.common.permissionGranted')}
          </Text>
        ),
    },
  ];

  if (
    (service === Service.OneDrive ||
      service === Service.SharePoint ||
      service === Service.Teams) &&
    window.SPOD_ACS_MANAGE_PERMISSION !== 'true'
  ) {
    const lang = i18next.language;
    const userConfigurationLink = {
      [Service.OneDrive]: getCasOLHLink(
        CAS_DOC_LINK.provision.oneDrive.userConfiguration,
        lang
      ),
      [Service.SharePoint]: getCasOLHLink(
        CAS_DOC_LINK.provision.sharePoint.userConfiguration,
        lang
      ),
      [Service.Teams]: getCasOLHLink(
        CAS_DOC_LINK.provision.teams.userConfiguration,
        lang
      ),
    }[service];

    stepItems.push({
      icon: <UserIcon />,
      title: (
        <Text {...titleTextStyle}>
          <Trans
            i18nKey={`provision.service.${service}.userConfiguration`}
            ns="provision"
          >
            placeholder
            <Link href={userConfigurationLink} target="_blank">
              placeholder
            </Link>
            placeholder
          </Trans>
        </Text>
      ),
      description: commonStatus > CommonStatus.NotProvision && (
        <Alert mt="8px" variant="outline" severity="info">
          {t('provision.common.assignAppIdTip', {
            appId: provisionStatus?.authorization?.apps?.[0].appId || '',
          })}
        </Alert>
      ),
    });
  }

  if (service === Service.Box) {
    stepItems.push({
      icon: <UserIcon />,
      title: (
        <Text {...titleTextStyle}>
          <Trans
            i18nKey={'provision.service.box.userConfiguration'}
            ns="provision"
          >
            placeholder
            <Link
              href={getCasOLHLink(
                CAS_DOC_LINK.provision.box.instruction,
                i18next.language
              )}
              target="_blank"
            >
              placeholder
            </Link>
            placeholder
          </Trans>
        </Text>
      ),
    });
  }

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (commonStatus === CommonStatus.NotProvision) {
      ProvisionApi.apiGetAuthenticationInfo(service, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
          if (
            service === Service.OneDrive ||
            service === Service.SharePoint ||
            service === Service.Teams
          ) {
            const errorCode = e.data?.error?.details?.[0].errorCode;
            if (
              errorCode === RejectProvisionReason.ServiceConflict ||
              errorCode === RejectProvisionReason.PocConflict
            ) {
              setRejectReason(errorCode);
            }
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={service}
        reason={rejectReason}
      />
    );
  }

  if (commonStatus === CommonStatus.NotProvision && !authenticationInfo) {
    return <></>;
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          {/* TODO: i18n: "Grant Access to Exchange Online" */}
          <ModalHeader>{t(`provision.service.${service}.title`)} </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={commonStatus}
                items={stepItems}
              />
              {commonStatus === CommonStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={service}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(service, orgId);
                      setCommonStatus(CommonStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        service,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        setCommonStatus(CommonStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {commonStatus === CommonStatus.Provisioned && <DataSynced />}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CommonModal;

const GrantPermissionAndEnablePolicy = ({
  service,
  authenticationInfo,
  setCommonStatus,
  setErrorMsg,
  orgId,
}: {
  service:
    | Service.OneDrive
    | Service.SharePoint
    | Service.Teams
    | Service.Box
    | Service.Dropbox;
  authenticationInfo: AuthenticationInfoRes | null;
  setCommonStatus: (status: CommonStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { getPrimaryApp } = useProvisionEntityInfo();

  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });
  const { t } = useTranslation(['provision', 'common']);
  const [loading, toggleLoading] = useToggle(false);

  const cookieName = {
    [Service.OneDrive]: 'provision_status_azuread_graph_od',
    [Service.SharePoint]: 'provision_status_azuread_graph_sp',
    [Service.Teams]: 'provision_status_azuread_graph_teams',
    [Service.Box]: 'provision_status_box',
    [Service.Dropbox]: 'provision_status_dropbox',
  }[service];

  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service,
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const provisionInfo = getProvisionCookie(cookieName);
      if (provisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code, state } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          let res: AxiosResponse<any, any>;
          try {
            if (service === Service.Box || service === Service.Dropbox) {
              res = await ProvisionApi.apiSaveProvisionInfoForStorage(
                service,
                {
                  authCode: code,
                  csrfCode: state,
                },
                radioGroupRef.current.enabledPolicyType,
                orgId
              );
            } else {
              res = await ProvisionApi.apiSaveProvisionInfo(
                getPrimaryApp(service),
                code,
                radioGroupRef.current.enabledPolicyType,
                orgId
              );
            }
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.${service}.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setCommonStatus(CommonStatus.Provisioning);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t(`provision.service.${service}.error.deniedGrant`));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Flex direction="column" pt="16px" gap="16px">
        <Divider variant="dashed" orientation="horizontal" />
        <Text color={colorStyle.color.secondary}>
          {/* TODO: i18n "Select the policy to enable when the access grant is completed:" */}
          {t('provision.common.selectPolicyToEnable')}
        </Text>
        <EnablePolicyRadioGroup service={service} ref={radioGroupRef} />
        <Divider variant="dashed" orientation="horizontal" />
        <Box>
          <Button variant="secondary" borderRadius="2rem" onClick={onClick}>
            {t('provision.button.grantPermission')}
          </Button>
        </Box>
      </Flex>
    </>
  );
};
